import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "orders";

export function useOrderList(options?: {
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	status?: string | undefined;
	search?: string | undefined;
	orderDateFrom?: string | undefined;
	orderDateTo?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.opportunityId) params.set("opportunityId", options.opportunityId);
			if (options?.assignedTo) params.set("assignedTo", options.assignedTo);
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.orderDateFrom) params.set("orderDateFrom", options.orderDateFrom);
			if (options?.orderDateTo) params.set("orderDateTo", options.orderDateTo);
			return api.get<{ items: Order[]; total: number }>(`/api/orders?${params.toString()}`);
		},
	});
}

export function useOrderDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Order & { lines: OrderLine[] }>(`/api/orders/${id}`),
		enabled: !!id,
	});
}

export function useCreateOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateOrderInput) => api.post<Order>("/api/orders", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateOrderInput> }) =>
			api.patch<Order>(`/api/orders/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/orders/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useRetryOrderSync() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			api.post<{ ok: boolean; message: string }>(`/api/orders/${id}/retry-sync`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export interface ProductionUnitSummary {
	serialNo: string;
	status: string;
}

export interface ProductionLineUnits {
	lineNumber: number;
	units: ProductionUnitSummary[];
}

export function useProductionUnits(orderId: string) {
	return useQuery({
		queryKey: [KEY, "production-units", orderId],
		queryFn: () =>
			api.get<{ available: boolean; lines: ProductionLineUnits[] }>(
				`/api/orders/${orderId}/production-units`,
			),
		enabled: !!orderId,
	});
}

export interface ProductionStageLog {
	action: string;
	userName: string | null;
	createdAt: string;
	note: string | null;
	reason: string | null;
}

export interface ProductionStage {
	stageCode: string;
	stageName: string;
	sequenceNo: number;
	status: "done" | "in_progress" | "waiting" | "paused" | "planned";
	startedAt: string | null;
	finishedAt: string | null;
	logs: ProductionStageLog[];
}

export interface ProductionUnitDetail {
	serialNo: string;
	itemCode: string;
	itemName: string;
	stages: ProductionStage[];
}

export function useProductionUnitDetail(orderId: string, serialNo: string | null) {
	return useQuery({
		queryKey: [KEY, "production-unit-detail", orderId, serialNo],
		queryFn: () =>
			api.get<ProductionUnitDetail>(
				`/api/orders/${orderId}/production-units/${encodeURIComponent(serialNo ?? "")}`,
			),
		enabled: !!orderId && !!serialNo,
	});
}

interface Order {
	id: string;
	documentNumber: string | null;
	orderDate: string | null;
	deliveryDate: string | null;
	currency: string | null;
	exchangeRate: string | null;
	paymentTerms: string | null;
	deliveryTerms: string | null;
	notes: string | null;
	internalNotes: string | null;
	startupDescription: string | null;
	installationDescription: string | null;
	tmsDescription: string | null;
	atsDescription: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	subtotal: string | null;
	vatTotal: string | null;
	total: string | null;
	paidAmount: string | null;
	paymentStatus: "unpaid" | "partially_paid" | "paid" | "overpaid";
	isCustomDesign: boolean;
	status:
		| "draft"
		| "confirmed"
		| "partially_delivered"
		| "delivered"
		| "invoiced"
		| "closed"
		| "cancelled";
	companyId: string | null;
	contactPersonId: string | null;
	opportunityId: string | null;
	sourceOfferId: string | null;
	assignedTo: string | null;
	teamId: string | null;
	visibility: string;
	companyName: string | null;
	sapDocEntry: number | null;
	sapDocNum: number | null;
	syncStatus: "pending" | "in_progress" | "synced" | "failed" | null;
	syncError: string | null;
	productionSummary: { totalUnits: number; doneUnits: number } | null;
	createdAt: string;
	updatedAt: string;
}

interface OrderLine {
	id: string;
	lineNumber: number;
	itemCode: string | null;
	itemDescription: string | null;
	displayDescription: string | null;
	quantity: string;
	unit: string | null;
	unitPrice: string;
	vatRate: string | null;
	vatGroup: string | null;
	discountPercent: string | null;
	lineTotal: string | null;
	notes: string | null;
	stockItemId: string | null;
	warehouseId: string | null;
	warehouseName: string | null;
}

interface CreateOrderInput {
	orderDate?: string | undefined;
	deliveryDate?: string | undefined;
	currency?: string | undefined;
	exchangeRate?: number | undefined;
	paymentTerms?: string | undefined;
	deliveryTerms?: string | undefined;
	notes?: string | undefined;
	internalNotes?: string | undefined;
	companyId?: string | undefined;
	contactPersonId?: string | undefined;
	opportunityId?: string | undefined;
	sourceOfferId?: string | undefined;
	assignedTo?: string | undefined;
	subtotal?: number | undefined;
	vatTotal?: number | undefined;
	total?: number | undefined;
	paidAmount?: number | undefined;
	paymentStatus?: "unpaid" | "partially_paid" | "paid" | "overpaid" | undefined;
	lines: {
		lineNumber: number;
		itemCode?: string | undefined;
		itemDescription?: string | undefined;
		displayDescription?: string | undefined;
		quantity: number;
		unit?: string | undefined;
		unitPrice: number;
		vatRate?: number | undefined;
		vatGroup?: string | undefined;
		discountPercent?: number | undefined;
		lineTotal?: number | undefined;
		notes?: string | undefined;
		stockItemId?: string | undefined;
		warehouseId?: string | undefined;
	}[];
}
